import { Injectable } from '@angular/core';
import { DropdownItem } from '../../models/dropdown-item';
import { Observable } from 'rxjs/Observable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { CommonDataHttpService } from './common-data.http-service';
import { FlashMessagesService } from '../../../shared/flash-messages/service/flash.messages.service';
import { CommonDataMapper } from './common-data.mapper';
import { State } from '../../models/common-data/state';
import { PioneerUtils } from '../../utils/pioneer-utils';
import { CommonDataStoreInterface } from './common-data.store.interface';
import { Constants } from '../../../shared/constants';
import { Currency } from '../../models/common-data/currency';
import { LRUCache } from 'app/event/utils/lru-cache';
import { LogService } from 'app/shared/log/log.service';
import { catchError, filter, take, tap } from 'rxjs/operators';
import { of } from 'rxjs/observable/of';

@Injectable()
export class CommonDataStore implements CommonDataStoreInterface {

  get commonDataHttpService() { return this._commonDataHttpService; }
  private states$: BehaviorSubject<DropdownItem[]> = new BehaviorSubject([]);
  private countries$: BehaviorSubject<DropdownItem[]> = new BehaviorSubject([]);
  private currencies$: BehaviorSubject<Currency[]> = new BehaviorSubject([]);
  private currenciesDrop$: BehaviorSubject<DropdownItem[]> = new BehaviorSubject([]);
  private statesCache: LRUCache<string, BehaviorSubject<State[] | null>>;
  private statesCacheLimit = 3; // The cache will store a maximum of 3 arrays of states (from 3 countries)

  constructor(
    private _commonDataHttpService: CommonDataHttpService,
    private flashMessagesService: FlashMessagesService,
    private logService: LogService) {
      // Initialize LRU cache with a size limit of 3 behavior subjects to cache the states of 3 countries
      this.statesCache = new LRUCache<string, BehaviorSubject<State[] | null>>(this.statesCacheLimit);
    }

  loadCountries(): Observable<DropdownItem[]> {
    return this._commonDataHttpService.getCountries()
      .catch((error: any) => {
        this.flashMessagesService.error(
          'MESSAGES.ERROR.COULD_NOT_GET_COUNTRIES',
          `Error retrieving countries: ${error}`
        );
        return Observable.throw(error);
      })
      .map(countries => CommonDataMapper.convertCountriesToDropDownItem(countries))
      .map((countries: DropdownItem[]) => {
        this.countries$.next(countries);
        return countries;
      });
  }

  loadStatesFromCountry(countryCode: string): Observable<DropdownItem[]> {
    return this.getStates(countryCode)
      .map((states) => {
        return states.filter(state => CommonDataMapper.removeDuplicateStatesToDropDownItem(state));
      })
      .map(states => CommonDataMapper.convertStatesToDropDownItem(states))
      .map(states => {
        PioneerUtils.orderDropdownByValue(states);
        this.states$.next(states);
        return states;
      });
  }

  get states(): Observable<DropdownItem[]> {
    return this.states$.asObservable();
  }

  get countries(): Observable<DropdownItem[]> {
    return this.countries$.asObservable();
  }

  getCountry(countryCode: string): Observable<DropdownItem> {
      return this.countries$.flatMap((countries) => {
        return countries.filter(country => country.key === countryCode);
      });
  }

  getCurrency(currencyCode: string): Observable<DropdownItem> {
    return this.currenciesDrop$.flatMap((currencies) => {
      return currencies.filter(currency => currency.key === currencyCode);
    });
  }

  getState(countryCode, stateCode: string): Observable<DropdownItem> {
    stateCode = CommonDataMapper.fixDuplicateState(stateCode);
    return this.getStates(countryCode)
      .flatMap((states) => {
        return states.filter(state => PioneerUtils.safeTrim(state.stateCode) === stateCode);
      })
      .map(state => CommonDataMapper.convertStateToDropDownItem(state));
  }

  private getStatesFromApi(countryCode: string): Observable<State[]> {
    return this._commonDataHttpService.getStates(countryCode)
      .catch((error: any) => {
        this.flashMessagesService.error(
          'MESSAGES.ERROR.COULD_NOT_GET_STATES',
          `Error retrieving states: ${error}`
        );
        return Observable.throw(error);
      });
  }

  /**
   * This method fetches the states for a given country code from the API,
   * and caches the result to avoid redundant API calls.
   * It uses the LRU (Least Recently Used) cache eviction policy
   * to limit the cache size (max 3 arrays of states from 3 countries).
   * @param countryCode
   * @returns
   */
  getStates(countryCode: string): Observable<State[]> {
    this.logService.debug(`getStates from country code: ${countryCode}`);
    if (!countryCode || (Constants.COUNTRIES_WITH_STATES.indexOf(countryCode) < 0)) {
      this.logService.debug(`Country code ${countryCode} does not have states`);
      return of([]);
    }

    let cachedEntry = this.statesCache.get(countryCode);

    if (cachedEntry) {
      // Return the existing cached states if they are available
      return cachedEntry.asObservable().pipe(
        filter((states): states is State[] => states !== null)
      );
    }

    // If the cache does not contain the states,
    // fetch the states from the API and cache the result
    cachedEntry = new BehaviorSubject<State[] | null>(null);
    this.statesCache.add(countryCode, cachedEntry);

    this.getStatesFromApi(countryCode).pipe(
      take(1),
      tap((states: State[]) => {
        cachedEntry!.next(states);
      }),
      catchError((error: any) => {
        this.logService.error(`Error fetching states for ${countryCode}: ${error}`);
        cachedEntry!.error(error);
        return of([]);
      })
    ).subscribe();

    return cachedEntry.asObservable().pipe(
      filter((states): states is State[] => states !== null)
    );
  }

  loadCurrencies(): Observable<Currency[]> {
    return this._commonDataHttpService.getCurrencies()
      .catch((error: any) => {
        this.flashMessagesService.error(
          'MESSAGES.ERROR.COULD_NOT_GET_CURRENCIES',
          `Error retrieving currencies: ${error}`
        );
        return Observable.throw(error);
      })
      .map((currencies: Currency[]) => {
        this.currencies$.next(currencies);
        return currencies;
      });
  }

  loadCurrenciesDrop(): Observable<DropdownItem[]> {
    return this._commonDataHttpService.getCurrencies()
      .catch((error: any) => {
        this.flashMessagesService.error(
          'MESSAGES.ERROR.COULD_NOT_GET_COUNTRIES',
          `Error retrieving countries: ${error}`
        );
        return Observable.throw(error);
      })
      .map(currencies => CommonDataMapper.convertCurrenciesToDropDownItem(currencies))
      .map((currencies: DropdownItem[]) => {
        this.currenciesDrop$.next(currencies);
        return currencies;
      });
  }

  get currencies(): Observable<Currency[]> {
    return this.currencies$.asObservable();
  }

  get currenciesDrop(): Observable<DropdownItem[]> {
    return this.currenciesDrop$.asObservable();
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/service/common-data/common-data.store.ts